<template lang="pug">
#picture-list(v-cloak)
  .box.d-print-none
    .box-header
      .note
        span ご家族からの写真印刷依頼が表示されます。未配布の写真がある場合は、印刷してご利用者にお渡しください。
        br
        span ご利用者への配布後に未配布を押してください。ご家族に配布メッセージを自動配信いたします。
    .box-body
      b-tabs
        b-tab.tab(title="全て" active)
          PictureTable(ref="pictureTable" @print="print" @distribute="distribute" settingNameSuffix="all")
        b-tab.tab(title="未配布のみ")
          PictureTable(ref="undistributedPictureTable" distributed="false" @print="print" @distribute="distribute" settingNameSuffix="undistributed")
        b-tab.tab(title="配布済のみ")
          PictureTable(ref="distributedPictureTable" distributed="true" @print="print" settingNameSuffix="distributed")

  PicturePdfModal(ref="picturePdfModal")
  
  DistributePictureModal(ref="distributePictureModal" @submit="distributePicture")
  OkModal(v-model="showDistributeSuccessModal" title="配布に成功しました。" @ok="refresh")

  PageLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Ref, Vue } from "vue-property-decorator";
import { Route } from "vue-router";
import { userStore } from "@/store/store";

import PicturesService, { PicturesPatchModel } from "@/commons/services/Pictures";

import OkModal from "@/pc/basics/OkModal.vue";
import PictureTable, { PictureTableItem } from "@/pc/containers/pictures/PictureTable.vue";
import PicturePdfModal from "@/pc/components/modals/PicturePdfModal.vue";
import DistributePictureModal from "@/pc/components/modals/DistributePictureModal.vue";
import PageLoading from "@/pc/basics/PageLoading.vue";

/** ナビゲーションガードのフックを使えるようにする */
Component.registerHooks(["beforeRouteLeave"]);

/**
 * 写真印刷依頼画面コンポーネント
 */
@Component({
  components: {
    PageLoading,
    OkModal,
    PictureTable,
    PicturePdfModal,
    DistributePictureModal
  }
})
export default class PictureList extends Vue {
  /** 写真サービス */
  @Inject("PicturesService") picturesService!: PicturesService;

  @Ref()
  pictureTable!: PictureTable;
  @Ref()
  undistributedPictureTable!: PictureTable;
  @Ref()
  distributedPictureTable!: PictureTable;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";
  /** 配布完了モーダル表示可否 */
  showDistributeSuccessModal: boolean = false;
  /** 配布対象の写真 */
  distributeTargetPicture: any | PictureTableItem = "";

  /**
   * vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    return;
  }

  /**
   * 印刷ボタン押下処理
   *
   * ブラウザの印刷プレビューを表示する
   * 未配布の写真の場合、印刷プレビューが閉じられた後に写真配布APIを呼び出して配布する
   */
  public async print(picture: PictureTableItem): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    // プレビュー表示は子コンポーネントに機能を委譲しているので、子コンポーネントを表示するのみ
    const printDialog: PicturePdfModal | Vue | Element | Vue[] | Element[] = this.$refs.picturePdfModal;
    if (printDialog instanceof PicturePdfModal) {
      await printDialog.show(picture.id);
    }

    this.isBusy = false;

    return;
  }

  /**
   * 配布ボタン押下処理
   */
  public async distribute(picture: PictureTableItem): Promise<void> {
    // 配布対象の写真を一時保持する
    this.distributeTargetPicture = picture;

    // 配布確認用のオリジナルモーダルを表示する
    const distributeModal: DistributePictureModal = this.$refs.distributePictureModal as DistributePictureModal;
    if (distributeModal) {
      distributeModal.show(`${picture.residentRoom}の${picture.residentName}様に写真をお渡しされましたか？`, picture.rawReceiveDatetime, picture.residentName);
    }

    return;
  }

  /**
   * 写真を配布する
   */
  public async distributePicture(result: { sendMessage: boolean; text: string }): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "配布中...";
    const distributeModal: any = this.$refs.distributePictureModal;

    try {
      const requestBody: PicturesPatchModel = {
        distributedId: userStore.loginUser.userId,
        distributedName: userStore.loginUser.name,
        distributedDatetime: this.$dateUtil.convertDateToNumber(new Date(), "yyyymmddhhMMss000"),
        sendMessage: result.sendMessage,
        text: result.text
      };

      await this.picturesService.patch(userStore, this.distributeTargetPicture.id, requestBody);

      // 配布成功のため、配布確認モーダルを非表示にする
      if (distributeModal) {
        distributeModal.hide();
      }

      // 配布成功ダイアログを表示する
      this.showDistributeSuccessModal = true;
    } catch (error) {
      // エラー発生時は、エラー画面遷移前に最前面にモーダルが残るのを防ぐため非表示にする
      if (distributeModal) {
        distributeModal.hide();
      }
      throw error;
    } finally {
      this.isBusy = false;
    }

    return;
  }

  public refresh(): void {
    // 配布/未配布を変更すると全タブに影響があるため、各タブを更新する
    const pictureTable: any = this.$refs.pictureTable;
    if (pictureTable) {
      pictureTable.refresh();
    }

    const undistributedPictureTable: any = this.$refs.undistributedPictureTable;
    if (undistributedPictureTable) {
      undistributedPictureTable.refresh();
    }

    const distributedPictureTable: any = this.$refs.distributedPictureTable;
    if (distributedPictureTable) {
      distributedPictureTable.refresh();
    }

    // サイドメニューの未完了バッジを更新する
    this.$emit("refresh");

    return;
  }

  beforeRouteLeave(to: Route, from: Route, next: Function) {
    // 画面を離れる前に現在のフィルタリング条件をVuexに保存
    if (this.$options.name) {
      this.pictureTable.setFilterSetting();
      this.undistributedPictureTable.setFilterSetting();
      this.distributedPictureTable.setFilterSetting();
    }
    next();
  }
}
</script>

<style lang="scss" scoped>
#picture-list {
  max-width: 1280px;
  margin-left: auto;
  margin-right: auto;

  .box {
    .tab {
      margin-top: 24px;
    }
  }
}
</style>
